import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Send, Info, CheckCircle2, Copy, Search, Download } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { generateApplicationReceipt } from "../lib/pdf";
import { useT } from "../lib/i18n";
import { LanguageToggle } from "../components/LanguageToggle";
import { PhoneVerify } from "../components/PhoneVerify";
import { PrivacyNotice } from "../components/PrivacyNotice";
import { ErrorSummary } from "../components/ErrorSummary";
import { SaveToServerDialog, ResumeBox, AssistanceDialog, EvidenceStep, ReviewStep } from "../components/ApplyJourney";
import { useSearchParams } from "react-router-dom";
import { ADAMAWA_LGAS, NIGERIA_STATES } from "../lib/nigeria";

const APPLICANT_TYPES = [
  ["individual", "Individual"],
  ["family", "Family / Household"],
  ["community", "Community"],
  ["organisation", "Organisation / Group"],
];

const CATEGORIES = [
  ["individual", "Individual support"],
  ["family", "Family support"],
  ["empowerment", "Empowerment / Livelihood"],
  ["food", "Food support"],
  ["emergency", "Emergency assistance"],
  ["education", "Education support"],
  ["health", "Health intervention"],
  ["water", "Water / Sanitation"],
  ["energy", "Energy / Solar"],
  ["agriculture", "Agriculture / Food security"],
  ["community", "Community project"],
  ["women_youth", "Women & Youth empowerment"],
  ["organisation", "Organisation partnership"],
  ["partnership", "Partnership / Joint programme"],
];

const HELP_INDIVIDUAL = [
  "financial_hardship", "food_support", "emergency_assistance", "education_support",
  "school_fees", "skills_training", "business_startup", "business_expansion",
  "tools_equipment", "farming_support", "womens_empowerment", "youth_empowerment",
  "livelihood_support", "disability_support", "elderly_support", "housing_emergency",
];
const HELP_COMMUNITY = [
  "water_supply", "borehole", "electricity_solar", "school_infrastructure",
  "educational_materials", "healthcare", "medical_outreach", "community_centre",
  "roads_access", "sanitation", "environmental", "agriculture_food_security",
  "women_youth_development", "vocational_centre", "community_economic",
  "emergency_disaster", "other_community",
];
const HELP_ORG = [
  "partnership_request", "programme_funding", "equipment_support", "capacity_building",
  "training", "collaboration", "research", "technical_assistance", "volunteer_support",
  "humanitarian_intervention", "joint_programme", "sponsorship",
];

const NICE = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const EMPTY = {
  applicant_type: "individual",
  primary_category: "empowerment",
  help_needs: [],
  full_name: "", date_of_birth: "", gender: "",
  phone: "", whatsapp: "", email: "",
  address: "", state: "Adamawa", lga: "", ward: "", community: "", phone_verification_token: "", phone_verified_flag: false,
  occupation: "", employment_status: "unemployed", dependants: 0, preferred_language: "English",
  household_adults: 0, household_children: 0, household_size: 0,
  main_income_source: "", monthly_household_income: 0,
  community_name: "", community_population: 0, community_leader: "", youth_rep: "", women_rep: "", community_position: "",
  organisation_name: "", organisation_type: "", registration_number: "", year_established: "",
  contact_person: "", position: "", website: "", office_address: "", primary_area_of_work: "", people_served: 0,
  problem_description: "", problem_duration: "", who_is_affected: "", seriousness: "moderate",
  support_description: "", estimated_value: 0, needs_assessment: false, assistance_type: "financial",
  expected_impact: "",
  empowerment_activity: "", empowerment_plan: "", experience: "", support_use: "", self_reliance_plan: "",
  community_need: "", beneficiaries_count: 0, community_discussed: "no", community_participation: "not_yet",
  community_contributions: [], maintenance_plan: "", community_priority: "high",
  organisation_description: "", proposed_intervention: "", beneficiaries_description: "",
  proposed_beneficiaries_count: 0, total_project_cost: 0, amount_requested: 0,
  other_funding: "no", other_funding_details: "", organisation_contribution: [],
  previous_support: "no", previous_support_details: "", other_orgs: "no", other_orgs_details: "",
  document_urls: [], document_notes: "",
  country: "Nigeria", location_description: "", landmark: "", gps: "",
  urgency_timeline: "within_30_days", if_no_intervention: "",
  verifier_name: "", verifier_position: "", verifier_organisation: "", verifier_phone: "", verifier_email: "",
  consent_true: false, consent_verification: false, consent_processing: false,
};

const STEPS = ["About You", "What Help Is Needed", "Household / Community", "Evidence", "Consent", "Review"];
const GROUPS = [[0, 2], [1, 3, 4], [5], ["evidence"], [6], ["review"]];
const DRAFT_KEY = "dkf_apply_draft_v1";

export default function Apply() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [draft, setDraft] = useState(() => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)); } catch { return null; } });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sp] = useSearchParams();
  const [showResume, setShowResume] = useState(sp.get("resume") === "1");
  const [serverSave, setServerSave] = useState(false);
  const [assist, setAssist] = useState(false);
  const visible = new Set(GROUPS[step]);
  const [errors, setErrors] = useState([]);
  const stepErrors = () => {
    const e = [];
    if (step === 0) {
      if (!form.applicant_type) e.push({ message: t("Choose who is applying") });
      if (!form.primary_category) e.push({ message: t("Choose the main type of support") });
      if (!form.full_name) e.push({ field: "apl-full-name", message: t("Enter your full name") });
      if (!form.phone) e.push({ field: "apl-phone", message: t("Enter a phone number") });
      if (!form.phone_verification_token) e.push({ message: t("Verify your phone number with the code we send you") });
    }
    if (step === 1) {
      if (!form.help_needs.length) e.push({ message: t("Select at least one kind of help") });
      if (!form.problem_description) e.push({ field: "apl-problem", message: t("Describe the problem") });
      if (!form.seriousness) e.push({ message: t("Tell us how serious the situation is") });
      if (!form.support_description) e.push({ field: "apl-support", message: t("Describe the support you are asking for") });
    }
    if (step >= 4 && !(form.consent_true && form.consent_verification && form.consent_processing)) e.push({ message: t("Tick all three consent statements") });
    return e;
  };
  const nav = useNavigate();
  const t = useT();

  useEffect(() => { document.title = "Apply for Support | Dutsen Kauna Foundation"; }, []);
  const saveDraft = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, form, saved_at: new Date().toISOString() }));
    toast.success(t("Draft saved on this device. Come back anytime to continue."));
  };
  const resumeDraft = () => { if (draft) { setForm({ ...EMPTY, ...draft.form }); setStep(draft.step || 0); } setDraft(null); };
  const discardDraft = () => { localStorage.removeItem(DRAFT_KEY); setDraft(null); };
  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm((p) => ({
    ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
  }));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        dependants: Number(form.dependants) || 0,
        household_adults: Number(form.household_adults) || 0,
        household_children: Number(form.household_children) || 0,
        household_size: Number(form.household_size) || 0,
        monthly_household_income: Number(form.monthly_household_income) || 0,
        community_population: Number(form.community_population) || 0,
        people_served: Number(form.people_served) || 0,
        beneficiaries_count: Number(form.beneficiaries_count) || 0,
        proposed_beneficiaries_count: Number(form.proposed_beneficiaries_count) || 0,
        estimated_value: Number(form.estimated_value) || 0,
        total_project_cost: Number(form.total_project_cost) || 0,
        amount_requested: Number(form.amount_requested) || 0,
      };
      const { data } = await api.post("/applications", payload);
      localStorage.removeItem(DRAFT_KEY);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (result) {
    return (
      <div data-testid="apply-success" className="bg-[#f8f4e8] py-16 min-h-[80vh]">
        <div className="dk-container max-w-2xl">
          <div className="bg-white rounded-2xl p-10 text-center border border-[#d6aa43]/20">
            <CheckCircle2 className="text-[#d6aa43] mx-auto mb-4" size={48} />
            <h1 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mb-3">{t("Application successfully submitted")}</h1>
            <p className="text-[#0a0d12]/75 mb-6">{t("Thank you for submitting your request to Dutsen Kauna Foundation. Please save this serial number carefully.")}</p>
            <div className="p-6 rounded-xl bg-[#155e3f] text-white mb-6">
              <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] mb-2">{t("Your application serial number")}</div>
              <div className="font-mono text-2xl md:text-4xl font-bold text-[#d6aa43] mb-2" data-testid="application-serial">{result.serial}</div>
              <button onClick={() => navigator.clipboard.writeText(result.serial).then(() => toast.success(t("Copied")))} className="text-xs text-white inline-flex items-center gap-1"><Copy size={12} /> {t("Copy")}</button>
              <div className="text-xs text-white mt-3">{t("Category")}: {t(NICE(result.primary_category))}</div>
            </div>
            <div className="text-left p-4 rounded-md bg-[#f8f4e8] border border-[#d6aa43]/20 text-sm text-[#0a0d12]/75 mb-6">
              <Info className="inline text-[#d6aa43]" size={14} /> {t("Submitting an application does not guarantee approval or support. You'll be contacted for verification if additional information is needed.")}
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => generateApplicationReceipt({
                  serial: result.serial,
                  applicantName: form.full_name || form.organisation_name || form.community_name || "Applicant",
                  category: result.primary_category,
                  categoryLabel: NICE(result.primary_category),
                  dateISO: result.created_at,
                  trackUrl: `${window.location.origin}/apply/track?serial=${encodeURIComponent(result.serial)}`,
                }).catch(() => toast.error("Could not generate PDF"))}
                className="dk-btn-primary"
                data-testid="download-receipt-btn"
              >
                {t("Download receipt (PDF)")} <Download size={14} />
              </button>
              <button onClick={() => nav(`/apply/track?serial=${encodeURIComponent(result.serial)}`)} className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]" data-testid="track-my-application">{t("Track my application")} <Search size={14} /></button>
              <Link to="/" className="text-sm text-[#0d1724] underline underline-offset-4 self-center">{t("Return home")}</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const canNext = () => {
    if (step === 0) return !!form.applicant_type && !!form.primary_category && oldOk(2);
    if (step === 1) return form.help_needs.length > 0 && oldOk(3) && oldOk(4);
    if (step === 2) return true;
    if (step === 3) return true;
    if (step === 4) return form.consent_true && form.consent_verification && form.consent_processing;
    if (step === 5) return form.consent_true && form.consent_verification && form.consent_processing;
    return true;
  };
  const oldOk = (old) => {
    if (old === 2) return !!form.phone && !!form.email && !!form.phone_verification_token && (form.applicant_type !== "individual" || !!form.full_name)
      && (form.applicant_type !== "community" || !!form.community_name)
      && (form.applicant_type !== "organisation" || !!form.organisation_name);
    if (old === 3) return !!form.problem_description && !!form.seriousness;
    if (old === 4) return !!form.support_description;
    return true;
    return true;
  };

  return (
    <div data-testid="apply-page">
      <section className="relative bg-[#155e3f] text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]" />
        <div className="dk-container relative">
          <div className="flex flex-wrap items-center justify-between gap-3"><span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">{t("Apply for Support")}</span><LanguageToggle /></div>
          <h1 className="font-serif text-3xl md:text-5xl font-bold mt-3 max-w-3xl">{t("Tell us what you need. Let us understand the situation.")}</h1>
          <p className="mt-4 text-white max-w-2xl">{t("Applications are 100% free. Submitting does not guarantee support — every request is reviewed against need, resources and programme priorities.")}</p>
        </div>
      </section>

      <section className="py-12 bg-[#f8f4e8]">
        <div className="dk-container max-w-4xl">
          {draft && (
            <div className="mb-6 p-4 rounded-xl bg-white border border-[#d6aa43]/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="apply-draft-banner">
              <div className="text-sm text-[#0a0d12]"><strong>{t("You have a saved draft")}</strong> {t("from")} {new Date(draft.saved_at).toLocaleString()}. {t("Continue where you left off?")}</div>
              <div className="flex gap-2">
                <button onClick={resumeDraft} className="dk-btn-primary !py-2 !px-4 text-xs" data-testid="apply-resume-draft">{t("Continue draft")}</button>
                <button onClick={discardDraft} className="text-xs px-3 text-[#0a0d12]/60" data-testid="apply-discard-draft">{t("Start fresh")}</button>
              </div>
            </div>
          )}
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="apply-stepper">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2 flex-shrink-0" data-testid={`apply-step-${i + 1}`} aria-current={i === step ? "step" : undefined}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i <= step ? "bg-[#d6aa43] text-[#0a0d12]" : "bg-white text-[#0a0d12]/40 border border-black/10"}`}>{i + 1}</div>
                <span className={`text-xs font-semibold uppercase tracking-[0.15em] ${i === step ? "text-[#0a0d12]" : "text-[#0a0d12]/40 hidden md:inline"}`}>{t(label)}</span>
                {i < STEPS.length - 1 && <ChevronRight size={14} className="text-[#0a0d12]/30" />}
              </div>
            ))}
          </div>

          {showResume && <ResumeBox onResume={(d) => { setForm({ ...EMPTY, ...d.form }); setStep(Math.min(d.step || 0, STEPS.length - 1)); setShowResume(false); }} />}
          <SaveToServerDialog open={serverSave} onOpenChange={setServerSave} form={form} step={step} />
          <AssistanceDialog open={assist} onOpenChange={setAssist} />
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-black/5" data-testid="apply-form" aria-labelledby="apply-step-heading">
            <h2 id="apply-step-heading" className="sr-only">{t("Step")} {step + 1} {t("of")} {STEPS.length}: {t(STEPS[step])}</h2>
            <ErrorSummary errors={errors} testid="apply-error-summary" />
            <div className="mb-6 space-y-3">
              <PrivacyNotice />
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <span className="font-semibold text-[#8a6a1f]">{t("Application is free.")}</span>
                <button type="button" onClick={() => setAssist(true)} className="underline text-[#0d1724]" data-testid="apply-request-assistance">{t("Request assistance completing this form")}</button>
                {!showResume && <button type="button" onClick={() => setShowResume(true)} className="underline text-[#0d1724]" data-testid="apply-show-resume">{t("Continue a saved application")}</button>}
              </div>
            </div>
            {visible.has(0) && (
              <div className="space-y-8">
                <div>
                  <h2 className="font-serif text-2xl text-[#0a0d12] font-bold mb-2">{t("Who is applying?")}</h2>
                  <div className="grid sm:grid-cols-2 gap-2 mt-4" data-testid="applicant-types">
                    {APPLICANT_TYPES.map(([k, l]) => (
                      <button key={k} type="button" onClick={() => up("applicant_type", k)}
                        className={`p-4 rounded-md text-left border-2 text-sm font-semibold transition ${form.applicant_type === k ? "bg-[#155e3f] text-[#d6aa43] border-[#0d1724]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}
                        data-testid={`applicant-type-${k}`}>{t(l)}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-[#0a0d12] font-bold mb-2">{t("Primary category")}</h2>
                  <p className="text-sm text-[#0a0d12]/70 mb-4">{t("This determines your application serial code (e.g. DKF-EMP, DKF-COM).")}</p>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="categories">
                    {CATEGORIES.map(([k, l]) => (
                      <button key={k} type="button" onClick={() => up("primary_category", k)}
                        className={`p-3 rounded-md text-left border-2 text-xs font-semibold transition ${form.primary_category === k ? "bg-[#d6aa43] border-[#d6aa43] text-[#0a0d12]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}
                        data-testid={`category-${k}`}>{t(l)}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {visible.has(1) && (
              <div className="space-y-8">
                <h2 className="font-serif text-2xl text-[#0a0d12] font-bold">{t("What kind of help are you requesting?")}</h2>
                <p className="text-sm text-[#0a0d12]/70">{t("Select all that apply.")}</p>
                <HelpGroup title="Individual & family" items={HELP_INDIVIDUAL} form={form} toggle={(v) => toggleArr("help_needs", v)} testidPrefix="help-ind" />
                <HelpGroup title="Community intervention" items={HELP_COMMUNITY} form={form} toggle={(v) => toggleArr("help_needs", v)} testidPrefix="help-com" />
                <HelpGroup title="Organisational support" items={HELP_ORG} form={form} toggle={(v) => toggleArr("help_needs", v)} testidPrefix="help-org" />
              </div>
            )}

            {visible.has(2) && (
              <div className="space-y-6">
                <h2 className="font-serif text-2xl text-[#0a0d12] font-bold">{t("Applicant details")}</h2>

                {form.applicant_type === "individual" && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <F label="Full name" required v={form.full_name} on={(v) => up("full_name", v)} tid="apl-full-name" />
                    <F label="Date of birth" type="date" v={form.date_of_birth} on={(v) => up("date_of_birth", v)} />
                    <F label="Gender" v={form.gender} on={(v) => up("gender", v)} />
                    <F label="Occupation" v={form.occupation} on={(v) => up("occupation", v)} />
                    <F label="Number of dependants" type="number" v={form.dependants} on={(v) => up("dependants", v)} />
                    <F label="Preferred language" v={form.preferred_language} on={(v) => up("preferred_language", v)} />
                  </div>
                )}

                {form.applicant_type === "family" && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <F label="Household representative" required v={form.full_name} on={(v) => up("full_name", v)} tid="apl-full-name" />
                    <F label="Main source of income" v={form.main_income_source} on={(v) => up("main_income_source", v)} />
                    <F label="Number of adults" type="number" v={form.household_adults} on={(v) => up("household_adults", v)} />
                    <F label="Number of children" type="number" v={form.household_children} on={(v) => up("household_children", v)} />
                    <F label="Total household size" type="number" v={form.household_size} on={(v) => up("household_size", v)} />
                    <F label="Estimated monthly income (₦)" type="number" v={form.monthly_household_income} on={(v) => up("monthly_household_income", v)} />
                  </div>
                )}

                {form.applicant_type === "community" && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <F label="Community name" required v={form.community_name} on={(v) => up("community_name", v)} tid="apl-community-name" />
                    <F label="Estimated population" type="number" v={form.community_population} on={(v) => up("community_population", v)} />
                    <F label="Community representative" v={form.full_name} on={(v) => up("full_name", v)} />
                    <F label="Position / Role" v={form.community_position} on={(v) => up("community_position", v)} />
                    <F label="Traditional / community leader" v={form.community_leader} on={(v) => up("community_leader", v)} />
                    <F label="Youth representative" v={form.youth_rep} on={(v) => up("youth_rep", v)} />
                    <F label="Women's representative" v={form.women_rep} on={(v) => up("women_rep", v)} />
                  </div>
                )}

                {form.applicant_type === "organisation" && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <F label="Organisation name" required v={form.organisation_name} on={(v) => up("organisation_name", v)} tid="apl-org-name" />
                    <F label="Organisation type" v={form.organisation_type} on={(v) => up("organisation_type", v)} />
                    <F label="Registration number" v={form.registration_number} on={(v) => up("registration_number", v)} />
                    <F label="Year established" v={form.year_established} on={(v) => up("year_established", v)} />
                    <F label="Contact person" v={form.contact_person} on={(v) => up("contact_person", v)} />
                    <F label="Position" v={form.position} on={(v) => up("position", v)} />
                    <F label="Website" v={form.website} on={(v) => up("website", v)} />
                    <F label="People served (estimate)" type="number" v={form.people_served} on={(v) => up("people_served", v)} />
                    <F label="Primary area of work" v={form.primary_area_of_work} on={(v) => up("primary_area_of_work", v)} className="sm:col-span-2" />
                  </div>
                )}

                <div>
                  <h3 className="font-serif text-lg text-[#0a0d12] font-bold mb-3">{t("Contact & location")}</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <F label="Phone" required v={form.phone} on={(v) => { up("phone", v); if (form.phone_verification_token) up("phone_verification_token", ""); }} tid="apl-phone" />
                    <div className="sm:col-span-2"><PhoneVerify phone={form.phone} token={form.phone_verification_token} onToken={(t, verified) => { up("phone_verification_token", t); up("phone_verified_flag", verified); }} /></div>
                    <F label="WhatsApp" v={form.whatsapp} on={(v) => up("whatsapp", v)} />
                    <F label="Email" type="email" required v={form.email} on={(v) => up("email", v)} className="sm:col-span-2" tid="apl-email" />
                    <F label="Address" v={form.address || form.office_address} on={(v) => up(form.applicant_type === "organisation" ? "office_address" : "address", v)} className="sm:col-span-2" />
                    <F label="State" v={form.state} on={(v) => up("state", v)} options={NIGERIA_STATES} tid="apl-state" />
                    <F label="LGA" v={form.lga} on={(v) => up("lga", v)} list={form.state === "Adamawa" ? ADAMAWA_LGAS : undefined} tid="apl-lga" />
                    <F label="Ward" v={form.ward} on={(v) => up("ward", v)} />
                    <F label="Community / Village" v={form.community} on={(v) => up("community", v)} />
                  </div>
                </div>
              </div>
            )}

            {visible.has(3) && (
              <div className="space-y-6">
                <h2 className="font-serif text-2xl text-[#0a0d12] font-bold">{t("Tell us about the problem")}</h2>
                <TA label="What is happening? Describe the situation clearly" required v={form.problem_description} on={(v) => up("problem_description", v)} tid="apl-problem" />
                <div>
                  <Label>How long has this problem existed?</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      ["lt_1m", "< 1 month"], ["1_6m", "1–6 months"], ["6_12m", "6–12 months"],
                      ["1_3y", "1–3 years"], ["gt_3y", "> 3 years"], ["long_standing", "Long-standing"],
                    ].map(([k, l]) => (
                      <RadioBtn key={k} active={form.problem_duration === k} onClick={() => up("problem_duration", k)}>{l}</RadioBtn>
                    ))}
                  </div>
                </div>
                <TA label="Who is affected? How many people?" v={form.who_is_affected} on={(v) => up("who_is_affected", v)} />
                <div>
                  <Label>How serious is the situation?</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {[["low", "Low"], ["moderate", "Moderate"], ["serious", "Serious"], ["very_serious", "Very serious"], ["emergency", "Emergency"]].map(([k, l]) => (
                      <RadioBtn key={k} active={form.seriousness === k} onClick={() => up("seriousness", k)}>{l}</RadioBtn>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {visible.has(4) && (
              <div className="space-y-6">
                <h2 className="font-serif text-2xl text-[#0a0d12] font-bold">{t("What support are you requesting?")}</h2>
                <TA label="Describe the support you need — exactly what should the Foundation do?" required v={form.support_description} on={(v) => up("support_description", v)} tid="apl-support" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <F label="Estimated value required (₦)" type="number" v={form.estimated_value} on={(v) => up("estimated_value", v)} />
                  <label className="flex items-center gap-2 pt-6 text-sm">
                    <input type="checkbox" checked={form.needs_assessment} onChange={(e) => up("needs_assessment", e.target.checked)} data-testid="apl-needs-assessment" />
                    {t("I need the Foundation to assess the cost.")}
                  </label>
                </div>
                <div>
                  <Label>Type of assistance required</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[["financial", "Financial"], ["materials", "Materials"], ["equipment", "Equipment"], ["food", "Food"], ["training", "Training"], ["technical", "Technical"], ["construction", "Construction"], ["programme", "Programme"], ["partnership", "Partnership"], ["emergency", "Emergency"], ["advocacy", "Advocacy"], ["other", "Other"]].map(([k, l]) => (
                      <RadioBtn key={k} active={form.assistance_type === k} onClick={() => up("assistance_type", k)}>{l}</RadioBtn>
                    ))}
                  </div>
                </div>
                <TA label="What will change if support is provided? Expected impact" v={form.expected_impact} on={(v) => up("expected_impact", v)} />
              </div>
            )}

            {visible.has(5) && (
              <div className="space-y-8">
                <h2 className="font-serif text-2xl text-[#0a0d12] font-bold">{t("Additional details")}</h2>

                {["empowerment", "individual", "family"].includes(form.primary_category) && (
                  <div className="p-5 rounded-xl bg-[#f8f4e8] border border-[#d6aa43]/20 space-y-4">
                    <h3 className="font-serif text-lg text-[#0a0d12] font-bold">{t("Empowerment section")}</h3>
                    <F label="What do you want to do? (activity)" v={form.empowerment_activity} on={(v) => up("empowerment_activity", v)} />
                    <TA label="Describe your plan" v={form.empowerment_plan} on={(v) => up("empowerment_plan", v)} />
                    <TA label="What experience do you have?" v={form.experience} on={(v) => up("experience", v)} />
                    <TA label="What will the support be used for? List main items/expenses" v={form.support_use} on={(v) => up("support_use", v)} />
                    <TA label="How will this help you become more self-reliant?" v={form.self_reliance_plan} on={(v) => up("self_reliance_plan", v)} />
                  </div>
                )}

                {["community", "water", "energy", "agriculture", "health"].includes(form.primary_category) && (
                  <div className="p-5 rounded-xl bg-[#f8f4e8] border border-[#d6aa43]/20 space-y-4">
                    <h3 className="font-serif text-lg text-[#0a0d12] font-bold">{t("Community project section")}</h3>
                    <TA label="Describe the community need" v={form.community_need} on={(v) => up("community_need", v)} />
                    <F label="Number of people expected to benefit" type="number" v={form.beneficiaries_count} on={(v) => up("beneficiaries_count", v)} />
                    <div>
                      <Label>Has the community discussed this need?</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[["yes", "Yes"], ["no", "No"], ["ongoing", "Ongoing"]].map(([k, l]) => (
                          <RadioBtn key={k} active={form.community_discussed === k} onClick={() => up("community_discussed", k)}>{l}</RadioBtn>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Community willing to participate?</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[["yes", "Yes"], ["no", "No"], ["not_yet", "Not yet discussed"]].map(([k, l]) => (
                          <RadioBtn key={k} active={form.community_participation === k} onClick={() => up("community_participation", k)}>{l}</RadioBtn>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>What can the community contribute?</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {["Land", "Labour", "Security", "Local materials", "Committee", "Maintenance", "Financial", "Volunteers", "Monitoring"].map((c) => (
                          <label key={c} className={`p-2 rounded-md border-2 text-xs cursor-pointer flex items-center gap-2 ${form.community_contributions.includes(c) ? "border-[#d6aa43] bg-white" : "border-black/10"}`}>
                            <input type="checkbox" checked={form.community_contributions.includes(c)} onChange={() => toggleArr("community_contributions", c)} /> {t(c)}
                          </label>
                        ))}
                      </div>
                    </div>
                    <TA label="Who will maintain the project after completion?" v={form.maintenance_plan} on={(v) => up("maintenance_plan", v)} />
                  </div>
                )}

                {["organisation", "partnership"].includes(form.primary_category) && (
                  <div className="p-5 rounded-xl bg-[#f8f4e8] border border-[#d6aa43]/20 space-y-4">
                    <h3 className="font-serif text-lg text-[#0a0d12] font-bold">{t("Organisation section")}</h3>
                    <TA label="Describe your organisation" v={form.organisation_description} on={(v) => up("organisation_description", v)} />
                    <TA label="Proposed intervention / partnership" v={form.proposed_intervention} on={(v) => up("proposed_intervention", v)} />
                    <TA label="Who will benefit?" v={form.beneficiaries_description} on={(v) => up("beneficiaries_description", v)} />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <F label="Proposed beneficiaries count" type="number" v={form.proposed_beneficiaries_count} on={(v) => up("proposed_beneficiaries_count", v)} />
                      <F label="Total project cost (₦)" type="number" v={form.total_project_cost} on={(v) => up("total_project_cost", v)} />
                      <F label="Amount requested from DKF (₦)" type="number" v={form.amount_requested} on={(v) => up("amount_requested", v)} />
                    </div>
                    <div>
                      <Label>Does your organisation have other funding?</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[["yes", "Yes"], ["no", "No"], ["pending", "Pending"]].map(([k, l]) => (
                          <RadioBtn key={k} active={form.other_funding === k} onClick={() => up("other_funding", k)}>{l}</RadioBtn>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-5 rounded-xl bg-white border border-black/10 space-y-4">
                  <h3 className="font-serif text-lg text-[#0a0d12] font-bold">{t("Previous assistance")}</h3>
                  <div>
                    <Label>Have you previously received support from DKF?</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[["yes", "Yes"], ["no", "No"]].map(([k, l]) => (
                        <RadioBtn key={k} active={form.previous_support === k} onClick={() => up("previous_support", k)}>{l}</RadioBtn>
                      ))}
                    </div>
                  </div>
                  {form.previous_support === "yes" && (
                    <TA label="Programme, amount, date and outcome" v={form.previous_support_details} on={(v) => up("previous_support_details", v)} />
                  )}
                  <div>
                    <Label>Applied to other organisations for this same need?</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[["yes", "Yes"], ["no", "No"]].map(([k, l]) => (
                        <RadioBtn key={k} active={form.other_orgs === k} onClick={() => up("other_orgs", k)}>{l}</RadioBtn>
                      ))}
                    </div>
                  </div>
                  {form.other_orgs === "yes" && (
                    <TA label="Provide details" v={form.other_orgs_details} on={(v) => up("other_orgs_details", v)} />
                  )}
                </div>

                <div className="p-5 rounded-xl bg-white border border-black/10 space-y-4">
                  <h3 className="font-serif text-lg text-[#0a0d12] font-bold">{t("Location & urgency")}</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <F label="Country" v={form.country} on={(v) => up("country", v)} />
                    <F label="Nearest landmark" v={form.landmark} on={(v) => up("landmark", v)} />
                    <F label="GPS / map coordinates (optional)" v={form.gps} on={(v) => up("gps", v)} className="sm:col-span-2" />
                    <TA className="sm:col-span-2" label="Full location description" v={form.location_description} on={(v) => up("location_description", v)} />
                  </div>
                  <div>
                    <Label>When is the support needed?</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[["emergency", "Emergency"], ["within_7_days", "Within 7 days"], ["within_30_days", "Within 30 days"], ["within_3_months", "Within 3 months"], ["within_6_months", "Within 6 months"], ["long_term", "Long-term"]].map(([k, l]) => (
                        <RadioBtn key={k} active={form.urgency_timeline === k} onClick={() => up("urgency_timeline", k)}>{l}</RadioBtn>
                      ))}
                    </div>
                  </div>
                  <TA label="What may happen if no intervention is provided?" v={form.if_no_intervention} on={(v) => up("if_no_intervention", v)} />
                </div>

                <div className="p-5 rounded-xl bg-white border border-black/10 space-y-4">
                  <h3 className="font-serif text-lg text-[#0a0d12] font-bold">{t("Third-party verification contact")}</h3>
                  <p className="text-xs text-[#0a0d12]/70">{t("Someone who can independently confirm the situation (community leader, school head, religious leader, etc.).")}</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <F label="Name" v={form.verifier_name} on={(v) => up("verifier_name", v)} />
                    <F label="Position / relationship" v={form.verifier_position} on={(v) => up("verifier_position", v)} />
                    <F label="Organisation / community" v={form.verifier_organisation} on={(v) => up("verifier_organisation", v)} />
                    <F label="Phone" v={form.verifier_phone} on={(v) => up("verifier_phone", v)} />
                    <F label="Email" type="email" v={form.verifier_email} on={(v) => up("verifier_email", v)} className="sm:col-span-2" />
                  </div>
                </div>

              </div>
            )}

            {visible.has("evidence") && <EvidenceStep form={form} up={up} TA={TA} t={t} />}
            {visible.has("review") && <ReviewStep form={form} groups={GROUPS} goTo={setStep} t={t} />}
            {visible.has(6) && (
              <div className="space-y-5">
                <h2 className="font-serif text-2xl text-[#0a0d12] font-bold">{t("Consent & declaration")}</h2>
                <p className="text-sm text-[#0a0d12]/75">{t("Please confirm all statements before submitting.")}</p>
                <Consent v={form.consent_true} on={(x) => up("consent_true", x)} testid="consent-true">
                  I confirm that the information provided is true to the best of my knowledge. I understand providing false information may result in rejection, and submitting an application does not guarantee funding.
                </Consent>
                <Consent v={form.consent_verification} on={(x) => up("consent_verification", x)} testid="consent-verification">
                  I consent to appropriate verification. DKF may contact me or relevant persons to confirm the information.
                </Consent>
                <Consent v={form.consent_processing} on={(x) => up("consent_processing", x)} testid="consent-processing">
                  I consent to DKF securely processing this information for assessment, programme administration, and monitoring. Sensitive information will not be publicly published without permission.
                </Consent>
                <div className="p-4 rounded-md bg-[#f8f4e8] border border-[#d6aa43]/20 text-sm text-[#0a0d12]/75 flex gap-2">
                  <Info size={16} className="text-[#d6aa43] flex-shrink-0 mt-0.5" /> {t("All applications are free of charge. Never pay anyone to submit or influence a DKF decision.")}
                </div>
              </div>
            )}

            {/* Nav buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-black/5">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold text-[#0a0d12] disabled:opacity-40 hover:text-[#d6aa43]" data-testid="apply-back">
                <ChevronLeft size={16} /> {t("Back")}
              </button>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#0a0d12]/60">{t("Step")} {step + 1} {t("of")} {STEPS.length}</span>
                <button onClick={saveDraft} className="text-xs font-semibold text-[#0d1724] underline underline-offset-4 hover:text-[#d6aa43]" data-testid="apply-save-draft">{t("Save on this device")}</button>
                <button onClick={() => setServerSave(true)} className="text-xs font-semibold text-[#0d1724] underline underline-offset-4 hover:text-[#d6aa43]" data-testid="apply-save-server">{t("Save & continue later")}</button>
              </div>
              {step < STEPS.length - 1 ? (
                <button onClick={() => { const errs = stepErrors(); if (canNext() && !errs.length) { setErrors([]); setStep(step + 1); window.scrollTo({ top: 0 }); } else { setErrors(errs.length ? errs : [{ message: t("Please complete this step first.") }]); } }}
                  className={`dk-btn-primary !py-2.5 !px-5 text-sm ${canNext() ? "" : "opacity-50"}`} aria-disabled={!canNext()} data-testid="apply-next">
                  {t("Next")} <ChevronRight size={14} />
                </button>
              ) : (
                <button onClick={() => { if (canNext()) submit(); else toast.error(t("Please confirm all consent boxes.")); }} disabled={loading}
                  className="dk-btn-primary !py-2.5 !px-5 text-sm disabled:opacity-60" data-testid="apply-submit">
                  {loading ? t("Submitting…") : t("Submit application")} <Send size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function HelpGroup({ title, items, form, toggle, testidPrefix }) {
  const t = useT();
  return (
    <div>
      <h3 className="text-xs uppercase tracking-[0.2em] text-[#0d1724]/60 font-semibold mb-3">{t(title)}</h3>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid={`${testidPrefix}-group`}>
        {items.map((k) => (
          <label key={k} className={`p-2.5 rounded-md border-2 text-xs cursor-pointer flex items-center gap-2 ${form.help_needs.includes(k) ? "border-[#d6aa43] bg-[#f8f4e8]" : "border-black/10 hover:border-[#d6aa43]"}`}>
            <input type="checkbox" checked={form.help_needs.includes(k)} onChange={() => toggle(k)} data-testid={`${testidPrefix}-${k}`} />
            {t(NICE(k))}
          </label>
        ))}
      </div>
    </div>
  );
}

const Label = ({ children, htmlFor }) => { const t = useT(); return <label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2 block">{typeof children === "string" ? t(children) : children}</label>; };

function F({ label, v, on, type = "text", required, className = "", tid, options, list }) {
  const t = useT();
  const cls = "w-full px-4 py-2.5 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43] bg-white";
  const id = tid || `f-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className={className}>
      <Label htmlFor={id}>{t(label) + (required ? " *" : "")}</Label>
      {options ? (
        <select id={id} required={required} aria-required={required || undefined} value={v ?? ""} onChange={(e) => { on(e.target.value); e.target.removeAttribute("aria-invalid"); }} className={cls} data-testid={tid} data-field={tid}>
          <option value="">—</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input id={id} required={required} aria-required={required || undefined} type={type} value={v ?? ""} onChange={(e) => { on(e.target.value); e.target.removeAttribute("aria-invalid"); }} list={list ? `${tid || label}-list` : undefined} className={cls} data-testid={tid} data-field={tid} />
      )}
      {list && <datalist id={`${tid || label}-list`}>{list.map((o) => <option key={o} value={o} />)}</datalist>}
    </div>
  );
}
function TA({ label, v, on, required, className = "", tid }) {
  const t = useT();
  const id = tid || `ta-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className={className}>
      <Label htmlFor={id}>{t(label) + (required ? " *" : "")}</Label>
      <textarea id={id} required={required} aria-required={required || undefined} rows={4} value={v ?? ""} onChange={(e) => { on(e.target.value); e.target.removeAttribute("aria-invalid"); }}
        className="w-full px-4 py-3 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid={tid} data-field={tid} />
    </div>
  );
}
function RadioBtn({ active, onClick, children }) {
  const t = useT();
  return (
    <button type="button" onClick={onClick} className={`p-2.5 text-xs rounded-md font-semibold border-2 transition ${active ? "bg-[#155e3f] border-[#0d1724] text-[#d6aa43]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}>{typeof children === "string" ? t(children) : children}</button>
  );
}
function Consent({ v, on, children, testid }) {
  const t = useT();
  return (
    <label className={`flex items-start gap-3 p-4 rounded-md border-2 cursor-pointer ${v ? "border-[#d6aa43] bg-[#f8f4e8]" : "border-black/10 hover:border-[#d6aa43]"}`}>
      <input type="checkbox" checked={v} onChange={(e) => on(e.target.checked)} className="mt-1" data-testid={testid} />
      <span className="text-sm text-[#0a0d12]">{typeof children === "string" ? t(children.trim()) : children}</span>
    </label>
  );
}
